/**
 * "Once per shot TYPE per case, not once per case" (SPEC_CAPTURE_UX_R2 §4) — and a recapture of the same type
 * does not re-teach.
 *
 * Persisted per LINK (not per shot, not per device): re-opening the same link in the same browser must not
 * re-teach what has already been shown, while a different link starts fresh. The key never contains the link
 * token (`linkKey.ts`).
 */

import { linkScopedKey } from '@/capture/guidance/linkKey';
import { DEMO_TYPES, type DemoType } from './demoTypes';

const PREFIX = 'vcip-svapp-demos';

function storageKey(): string {
  return linkScopedKey(PREFIX);
}

export function seenDemos(): ReadonlySet<DemoType> {
  try {
    const raw = window.localStorage.getItem(storageKey()) ?? '';
    const known = new Set<string>(DEMO_TYPES);
    return new Set(raw.split(',').filter((t): t is DemoType => known.has(t)));
  } catch {
    return new Set();
  }
}

export function demoSeen(type: DemoType): boolean {
  return seenDemos().has(type);
}

export function markDemoSeen(type: DemoType): void {
  const next = new Set(seenDemos());
  next.add(type);
  try {
    window.localStorage.setItem(storageKey(), [...next].join(','));
  } catch {
    /* a device that refuses storage re-teaches — annoying, never broken */
  }
}

export function resetSeenDemosForTests(): void {
  try {
    window.localStorage.removeItem(storageKey());
  } catch {
    /* ignore */
  }
}
